'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ChipProps } from '@mui/material';
import {
  Plus,
  MoreHorizontal,
  Edit,
  X as CloseIcon,
  Download,
  Check,
  Clock,
  AlertTriangle,
  Paperclip,
  FileText,
  Receipt,
  CreditCard,
  Eye,
  Share2,
  Search,
  Repeat,
} from 'lucide-react';
import { filterDocuments, parseDocumentAmount as parseAmount } from './document-list-search';
import { DeleteInvoiceModal } from './delete-invoice-modal';
import { MarkAsPaidModal } from './mark-as-paid-modal';
import { ShareDocumentModal } from './share-document-modal';
import { toast } from '@/components/ui/use-toast';
import api from '@crm-src/lib/api';
import { downloadDocumentPdf } from '@crm-src/lib/document-pdf';
import { formatDate } from '@crm-src/lib/date-utils';
import { SearchInput } from './search-input';

export interface DocumentItem {
  id: string;
  number: string;
  date: string;
  due_date: string;
  total_amount: string | number;
  status: 'paid' | 'pending' | 'overdue' | 'expired' | 'converted' | 'rejected'; // For quotes: pending, expired, converted, rejected
  description?: string;
  type?: string;
  paid_date?: string;
  has_file?: boolean;
  file_url?: string;
  original_filename?: string;
  document_type?: 'invoice' | 'quote';
}

interface DocumentListProps {
  documents: DocumentItem[];
  documentType: 'invoice' | 'quote';
  customerId?: string;
  onDeleteDocument?: (documentId: string) => void;
  onDownloadFile?: (document: DocumentItem) => void;
  onUpdateStatus?: (documentId: string, status: 'paid' | 'pending', paidDate?: string) => void;
  onRefresh?: () => void;
  showNewButton?: boolean;
  formatCurrency?: (amount: number) => string;
  className?: string;
  // IDs of invoices that have active recurring schedules
  recurringInvoiceIds?: string[];
}

// Maps a quote status to a MUI Chip palette color (inlined during the MUI 5
// migration from the former Tailwind status-class badges).
function quoteStatusChip(status: DocumentItem['status']): { color: ChipProps['color']; label: string } | null {
  switch (status) {
    case 'pending':
      return { color: 'warning', label: 'Pending' };
    case 'expired':
      return { color: 'error', label: 'Expired' };
    case 'converted':
      return { color: 'success', label: 'Converted' };
    case 'rejected':
      return { color: 'default', label: 'Rejected' };
    default:
      return null;
  }
}

export function DocumentList({
  documents,
  documentType,
  customerId,
  onDeleteDocument,
  onDownloadFile,
  onUpdateStatus,
  onRefresh,
  showNewButton = true,
  formatCurrency = (amount) => `$${amount.toLocaleString()}`,
  className = '',
  recurringInvoiceIds = [],
}: DocumentListProps) {
  const navigate = useNavigate();

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedDocumentForShare, setSelectedDocumentForShare] = useState<DocumentItem | null>(null);
  const [markAsPaidModalOpen, setMarkAsPaidModalOpen] = useState(false);
  const [selectedDocumentForPaid, setSelectedDocumentForPaid] = useState<{
    id: string;
    invoice_number: string;
    total_amount: number;
  } | null>(null);
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);

  // Per-row actions menu anchor (keyed by document id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Auto-adjust page when documents change (e.g., after delete). Uses the SAME
  // filter as the rendered list (incl. amount matching) so the page math can't
  // drift from what's actually shown.
  useEffect(() => {
    const filteredDocs = filterDocuments(documents, searchQuery);

    // Check if current page is empty but there are documents
    const startIndex = (currentPage - 1) * itemsPerPage;
    const docsOnCurrentPage = filteredDocs.slice(startIndex, startIndex + itemsPerPage);

    if (docsOnCurrentPage.length === 0 && filteredDocs.length > 0 && currentPage > 1) {
      // Go to previous page
      setCurrentPage(prev => prev - 1);
    }
  }, [documents, searchQuery, currentPage, itemsPerPage]);

  // Search handlers passed to memoized SearchInput
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  // Helper to parse amount
  // Handle new document.
  // NOTE: the CRM route tree is mounted under `/crm` (see CrmRoutes.tsx), so
  // every in-CRM navigation MUST carry the `/crm` prefix. A bare `/invoices/…`
  // is an app-ROOT absolute path that misses the mount and falls through to the
  // catch-all (`<Route path="*">` → `/` → `/dashboard`) — that was the "View
  // invoice sends me to the front page" bug.
  const handleNewDocument = () => {
    const basePath = documentType === 'quote' ? '/crm/quotes' : '/crm/invoices';
    const url = customerId ? `${basePath}/generate?customerId=${customerId}` : `${basePath}/generate`;
    navigate(url);
  };

  // Handle edit document
  const handleEditDocument = (document: DocumentItem) => {
    const basePath = documentType === 'quote' ? '/crm/quotes' : '/crm/invoices';
    navigate(`${basePath}/generate?invoiceId=${document.id}&customerId=${customerId}`);
  };

  // Handle view document
  const handleViewDocument = (document: DocumentItem) => {
    const basePath = documentType === 'quote' ? '/crm/quotes' : '/crm/invoices';
    navigate(`${basePath}/${document.id}`);
  };

  // Handle delete
  const handleDeleteDocument = (document: DocumentItem) => {
    setSelectedDocumentId(document.id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (documentId: string) => {
    if (onDeleteDocument) {
      await onDeleteDocument(documentId);
    }
    setDeleteModalOpen(false);
    setSelectedDocumentId(null);
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle mark as paid (invoices only)
  const handleMarkAsPaid = (document: DocumentItem) => {
    const transformedDocument = {
      id: document.id || 'unknown',
      invoice_number: document.number || 'N/A',
      total_amount: parseAmount(document.total_amount),
    };
    setSelectedDocumentForPaid(transformedDocument);
    setMarkAsPaidModalOpen(true);
  };

  const handleMarkAsPaidConfirm = async (documentId: string, paidDate: string) => {
    if (!onUpdateStatus) return;

    setIsMarkingAsPaid(true);
    try {
      await onUpdateStatus(documentId, 'paid', paidDate);
      setMarkAsPaidModalOpen(false);
      setSelectedDocumentForPaid(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsMarkingAsPaid(false);
    }
  };

  // Handle share document
  const handleShareDocument = (document: DocumentItem) => {
    setSelectedDocumentForShare(document);
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
    setSelectedDocumentForShare(null);
  };

  // Handle PDF download — the one generator's PDF, fetched from crm-api.
  const handleDownloadPDF = async (document: DocumentItem) => {
    const noun = documentType === 'quote' ? 'Quote' : 'Invoice';
    try {
      await downloadDocumentPdf(document.id, {
        documentType: documentType === 'quote' ? 'quote' : 'invoice',
        number: document.number,
      });
      toast({ title: 'Success', description: `${noun} PDF downloaded successfully` });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({ title: 'Error', description: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  // Get icon based on document type
  const getIcon = () => {
    return documentType === 'quote' ? FileText : Receipt;
  };

  const Icon = getIcon();

  // Render empty state
  if (!documents || documents.length === 0) {
    return (
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>{documentType === 'quote' ? 'Quotes' : 'Invoices'}</Typography>
              </Box>
              {showNewButton && (
                <Button size="small" variant="outlined" color="inherit" startIcon={<Plus size={16} />} onClick={handleNewDocument}>
                  New {documentType === 'quote' ? 'Quote' : 'Invoice'}
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}><Icon size={48} /></Box>
            <Typography>No {documentType}s created yet</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render document list
  return (
    <>
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon size={20} />
                <Typography component="span" sx={{ fontWeight: 600 }}>{documentType === 'quote' ? 'Quotes' : 'Invoices'}</Typography>
              </Box>
              {showNewButton && (
                <Button size="small" variant="outlined" color="inherit" startIcon={<Plus size={16} />} onClick={handleNewDocument}>
                  New {documentType === 'quote' ? 'Quote' : 'Invoice'}
                </Button>
              )}
            </Box>
          }
        />
        <CardContent>
          {/* Memoized Search Input - isolated from parent re-renders */}
          <SearchInput
            placeholder={`Search ${documentType === 'quote' ? 'quotes' : 'invoices'}...`}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            searchQuery={searchQuery}
          />

          {(() => {
            // Filter documents based on search query (shared with the page-adjust effect)
            const filteredDocuments = filterDocuments(documents, searchQuery);

            // Show "no results" message if filtered list is empty
            if (filteredDocuments.length === 0) {
              const Icon = documentType === 'quote' ? FileText : Receipt;
              return (
                <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}><Icon size={48} /></Box>
                  <Typography>No {documentType}s found matching "{searchQuery}"</Typography>
                </Box>
              );
            }

            // Apply pagination to filtered results
            const paginatedDocuments = filteredDocuments.slice(
              (currentPage - 1) * itemsPerPage,
              currentPage * itemsPerPage
            );

            return (
              <>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {paginatedDocuments.map((doc) => (
              <Box key={doc.id} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper", "&:hover": { bgcolor: "action.hover" } }}>
                {/* Top row: number left, price right */}
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, justifyContent: { sm: "space-between" }, gap: 1, mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{ fontWeight: 500, fontSize: "1rem", color: "primary.main", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                      onClick={() => handleViewDocument(doc)}
                    >
                      {doc.number}
                    </Typography>
                    {/* Recurring indicator */}
                    {documentType === 'invoice' && recurringInvoiceIds.includes(doc.id) && (
                      <Tooltip title="This invoice has recurring billing enabled">
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 999,
                            bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                            color: "secondary.main",
                          }}
                        >
                          <Repeat size={12} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: { sm: "flex-end" } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>{formatCurrency(parseAmount(doc.total_amount))}</Typography>
                      {doc.has_file && <Box sx={{ display: "flex", color: "primary.main" }}><Paperclip size={16} /></Box>}
                    </Box>

                    {/* Actions menu */}
                    <IconButton size="small" sx={{ width: 32, height: 32 }} aria-label="Open menu" onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: doc.id })}>
                      <MoreHorizontal size={16} />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor?.id === doc.id ? menuAnchor.el : null}
                      open={menuAnchor?.id === doc.id}
                      onClose={() => setMenuAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                      <Divider />
                      <MenuItem onClick={() => { setMenuAnchor(null); handleViewDocument(doc); }}>
                        <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                        View {documentType === 'quote' ? 'Quote' : 'Invoice'}
                      </MenuItem>
                      <MenuItem onClick={() => { setMenuAnchor(null); handleEditDocument(doc); }}>
                        <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                        Edit {documentType === 'quote' ? 'Quote' : 'Invoice'}
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={() => { setMenuAnchor(null); handleShareDocument(doc); }}>
                        <Box sx={{ mr: 1, display: "flex" }}><Share2 size={16} /></Box>
                        Share
                      </MenuItem>
                      <MenuItem onClick={() => { setMenuAnchor(null); handleDownloadPDF(doc); }}>
                        <Box sx={{ mr: 1, display: "flex" }}><Download size={16} /></Box>
                        Download PDF
                      </MenuItem>
                      {onDownloadFile && doc.has_file && (
                        <MenuItem onClick={() => { setMenuAnchor(null); onDownloadFile(doc); }}>
                          <Box sx={{ mr: 1, display: "flex" }}><Paperclip size={16} /></Box>
                          Download File
                        </MenuItem>
                      )}

                      {/* Status options - only for invoices, not quotes */}
                      {documentType === 'invoice' && onUpdateStatus && (
                        <>
                          <Divider />
                          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Status</ListSubheader>
                          {doc.status !== 'paid' && (
                            <MenuItem onClick={() => { setMenuAnchor(null); handleMarkAsPaid(doc); }}>
                              <Box sx={{ mr: 1, display: "flex", color: "success.main" }}><Check size={16} /></Box>
                              Mark as Paid
                            </MenuItem>
                          )}
                          {doc.status === 'paid' && (
                            <MenuItem onClick={() => { setMenuAnchor(null); onUpdateStatus(doc.id, 'pending'); }}>
                              <Box sx={{ mr: 1, display: "flex", color: "text.secondary" }}><Clock size={16} /></Box>
                              Mark as Unpaid
                            </MenuItem>
                          )}
                        </>
                      )}

                      {/* Delete option */}
                      {onDeleteDocument && (
                        <>
                          <Divider />
                          <MenuItem
                            onClick={() => { setMenuAnchor(null); handleDeleteDocument(doc); }}
                            sx={{ color: "error.main" }}
                          >
                            <Box sx={{ mr: 1, display: "flex" }}><CloseIcon size={16} /></Box>
                            Delete {documentType === 'quote' ? 'Quote' : 'Invoice'}
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </Box>
                </Box>

                {/* Date grid: Issue, Due, Paid/Status */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: { xs: 1, sm: 2 }, fontSize: 14 }}>
                  {/* Issue Date */}
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ color: "text.disabled", fontWeight: 500, mb: 0.5, fontSize: { xs: 12, sm: 14 } }}>Issue</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: { xs: 12, sm: 14 } }}>
                      {formatDate(doc.date)}
                    </Typography>
                  </Box>

                  {/* Due Date */}
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ color: "text.disabled", fontWeight: 500, mb: 0.5, fontSize: { xs: 12, sm: 14 } }}>Due</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: { xs: 12, sm: 14 } }}>
                      {formatDate(doc.due_date)}
                    </Typography>
                  </Box>

                  {/* Paid Date - only show for invoices */}
                  {documentType === 'invoice' && (
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        sx={{ fontWeight: 500, mb: 0.5, fontSize: { xs: 12, sm: 14 }, color: doc.status === 'paid' ? "success.main" : "text.disabled" }}
                      >
                        Paid
                      </Typography>
                      <Typography
                        sx={{ fontSize: { xs: 12, sm: 14 }, color: doc.status === 'paid' ? "success.dark" : "text.disabled" }}
                      >
                        {doc.status === 'paid' && doc.paid_date
                          ? formatDate(doc.paid_date)
                          : '—'}
                      </Typography>
                    </Box>
                  )}

                  {/* For quotes, show status in third column */}
                  {documentType === 'quote' && (
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ color: "text.disabled", fontWeight: 500, mb: 0.5, fontSize: { xs: 12, sm: 14 } }}>Status</Typography>
                      <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        {(() => {
                          const chip = quoteStatusChip(doc.status);
                          return chip ? <Chip size="small" color={chip.color ?? "default"} label={chip.label} /> : null;
                        })()}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Status indicators - only for invoices */}
                {documentType === 'invoice' && (doc.status === 'pending' || doc.status === 'overdue') && (
                  <Box sx={{ mt: 1.5, textAlign: "center" }}>
                    {doc.status === 'pending' && (
                      <Chip size="small" color="warning" icon={<Clock size={12} />} label="Pending" />
                    )}
                    {doc.status === 'overdue' && (
                      <Chip size="small" color="error" icon={<AlertTriangle size={12} />} label="Overdue" />
                    )}
                  </Box>
                )}
              </Box>
            ))}
                </Box>

                {/* Pagination Controls */}
                {filteredDocuments.length > itemsPerPage && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                        {currentPage} / {Math.ceil(filteredDocuments.length / itemsPerPage)}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredDocuments.length / itemsPerPage), prev + 1))}
                        disabled={currentPage >= Math.ceil(filteredDocuments.length / itemsPerPage)}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Delete Modal */}
      <DeleteInvoiceModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedDocumentId(null);
        }}
        invoiceId={selectedDocumentId}
        onConfirmDelete={handleConfirmDelete}
        documentType={documentType}
      />

      {/* Mark as Paid Modal - Only for invoices */}
      {documentType === 'invoice' && (
        <MarkAsPaidModal
          isOpen={markAsPaidModalOpen}
          onClose={() => {
            setMarkAsPaidModalOpen(false);
            setSelectedDocumentForPaid(null);
          }}
          invoice={selectedDocumentForPaid}
          onConfirm={handleMarkAsPaidConfirm}
          isLoading={isMarkingAsPaid}
        />
      )}

      {/* Share Document Modal */}
      <ShareDocumentModal
        isOpen={shareModalOpen}
        onClose={handleShareModalClose}
        documentId={selectedDocumentForShare?.id || null}
        documentType={documentType}
        documentNumber={selectedDocumentForShare?.number}
      />
    </>
  );
}


